/**
 * @fileoverview Attachment thumbnail — resolves photo ID to visual thumbnail.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Uses the host-injected ThumbnailResolver to display visual photo thumbnails
 *   inside CameraField and PhotoField widgets, and the violation capture surface.
 * - Falls back to a placeholder icon when no resolver is injected or resolution fails.
 * - Optionally supports a tap-to-enlarge affordance via `onClick`.
 *
 * @reusable
 * Usage: Mounted by CameraField, PhotoField, and ViolationCaptureBlock for each attachment ref.
 * Rules: presentation-only; no side effects beyond thumbnail resolution.
 * Dependencies: react, ../contracts/rendererContext
 * Architecture: Keep behavior within the existing package and local-first boundaries.
 * Notes: Preserve existing behavior and synchronization invariants.
 */
import { useState, useEffect, useRef, type MouseEvent } from 'react';
import { useThumbnailResolver, useThumbnailUrlOwnership } from '../contracts/rendererContext';

/**
 * @component AttachmentThumb
 * Purpose: Render a local or remote attachment thumbnail.
 * Props: attachment id, optional alt text, click handler, and dimensions.
 * Behavior: Resolves through the host provider and displays a fallback chip when unavailable.
 * Data Source: Host-injected thumbnail resolver.
 * Constraints: Performs no network access directly.
 * Performance: Resolves only when the attachment id or resolver changes.
 * @errorHandling AttachmentThumb
 * Strategy: Use the implementation’s existing catch, return, and propagation behavior.
 * User Impact: Callers can surface operation failures without silently treating them as success.
 * Logging: Retain existing operation logging and avoid sensitive values.
 */
export function AttachmentThumb({
  attachmentId,
  alt,
  onClick,
  sizeClassName = 'w-12 h-12',
}: {
  attachmentId: string;
  alt?: string;
  /** When provided, the thumbnail becomes tappable (e.g. to open a full-screen lightbox). */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** Tailwind size classes for the thumbnail box. Defaults to `w-12 h-12`. */
  sizeClassName?: string;
}) {
  const resolver = useThumbnailResolver();
  // Resolvers that return a FRESH object URL per call declare 'owned'
  // ownership (customer portal downloads); cached resolvers (tenant
  // AttachmentUrlResolver, mobile IndexedDB) are 'shared' — revoking a
  // shared URL would poison the cache for every later mount.
  const urlOwnership = useThumbnailUrlOwnership();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!resolver);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Owned object URLs retain the blob until revoked — release when the
  // thumbnail is replaced or the component unmounts so form navigation
  // doesn't leak. Shared URLs are resolver-owned and never revoked.
  useEffect(() => {
    if (urlOwnership !== 'owned' || !url?.startsWith('blob:')) return;
    return () => URL.revokeObjectURL(url);
  }, [url, urlOwnership]);

  useEffect(() => {
    if (!resolver || !attachmentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    resolver(attachmentId).then((resolved) => {
      if (mountedRef.current) {
        setUrl(resolved);
        setLoading(false);
      } else if (urlOwnership === 'owned' && resolved?.startsWith('blob:')) {
        // Resolved after unmount — the owned object URL never reaches
        // `url` and escapes the cleanup effect, so revoke it here.
        URL.revokeObjectURL(resolved);
      }
    }).catch(() => {
      if (mountedRef.current) {
        setUrl(null);
        setLoading(false);
      }
    });
  }, [resolver, attachmentId]);

  const interactive = !!onClick;

  if (!resolver) {
    return (
      <span
        role="img"
        aria-label={alt ?? 'Attachment preview unavailable'}
        data-thumbnail-fallback="true"
        className={`${sizeClassName} rounded-lg bg-slate-100 text-[10px] text-slate-500 inline-flex items-center justify-center px-1 text-center shrink-0`}
      >
        Preview unavailable
      </span>
    );
  }
  if (loading) {
    return (
      <div className={`${sizeClassName} rounded-lg bg-slate-100 animate-pulse shrink-0`} />
    );
  }
  if (!url) {
    return (
      <span
        role="img"
        aria-label={alt ?? 'Attachment preview unavailable'}
        data-thumbnail-fallback="true"
        className={`${sizeClassName} rounded-lg bg-slate-100 text-[10px] text-slate-500 inline-flex items-center justify-center px-1 text-center shrink-0`}
      >
        Preview unavailable
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      aria-label={interactive ? `View ${alt ?? 'photo'} full screen` : undefined}
      className={`${sizeClassName} rounded-lg overflow-hidden shrink-0 border border-slate-200 shadow-xs p-0 ${
        interactive ? 'cursor-pointer hover:opacity-90 hover:shadow-md active:scale-95 transition-all' : 'cursor-default'
      }`}
    >
      <img
        src={url}
        alt={alt ?? 'Photo'}
        className="w-full h-full object-cover"
      />
    </button>
  );
}
